import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

export default function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const withdrawn = new URLSearchParams(location.search).get("reason") === "withdrawn";

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate(location.state?.from || "/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-6 py-16" data-testid="login-page">
      <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">{t("auth.signin_title")}</h1>
      <p className="mt-2 font-body text-sm text-[#5C6873]">{t("auth.signin_sub")}</p>

      {withdrawn && (
        <div className="mt-6 rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="login-withdrawn-notice">
          {t("auth.withdrawn_notice")}
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="email" className="font-body text-sm text-[#1A2024]">{t("auth.email")}</Label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
          />
        </div>
        <div>
          <Label htmlFor="password" className="font-body text-sm text-[#1A2024]">{t("auth.password")}</Label>
          <Input
            id="password"
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
          />
          <div className="mt-1.5 text-right">
            <Link to="/forgot-password" className="font-body text-sm text-[#2E3A46] hover:underline" data-testid="login-forgot-link">
              {t("auth.forgot")}
            </Link>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="login-error">
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="h-11 w-full bg-[#2E3A46] text-white hover:bg-[#1A2024]"
        >
          {loading ? t("auth.signing_in") : t("auth.signin_title")}
        </Button>
      </form>

      <div className="mt-6 font-body text-sm text-[#5C6873]">
        {t("auth.no_account")}{" "}
        <Link to="/register" className="font-medium text-[#2E3A46] hover:underline" data-testid="login-to-register">
          {t("auth.create_account")}
        </Link>
      </div>
    </div>
  );
}
